/**
 * @fileoverview API helpers for linking payments to indirect costs.
 * @module features/indirect-costs/api/indirectCostsLinksService
 */

import apiClient from '@/lib/apiClient'
import {
  indirectCostLinksResponseSchema,
  type IndirectCostLinksResponse,
} from '../schemas/indirectCostsSchema'

export interface CreateCostFromPaymentsRequest {
  paymentUuids: string[]
  subcategoryUuid: string
  effectiveFrom: string
  effectiveTo: string
  description?: string
}

export async function createCostFromPayments(
  body: CreateCostFromPaymentsRequest
): Promise<IndirectCostLinksResponse> {
  const response = await apiClient.post('/indirect-costs/from-payments', body)
  const parsed = indirectCostLinksResponseSchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for POST /indirect-costs/from-payments')
  }
  return parsed.data
}

export interface LinkPaymentsRequest {
  paymentUuids: string[]
}

export async function linkPaymentsToCost(
  costUuid: string,
  body: LinkPaymentsRequest
): Promise<IndirectCostLinksResponse> {
  const response = await apiClient.post(
    `/indirect-costs/${encodeURIComponent(costUuid)}/links`,
    body
  )
  const parsed = indirectCostLinksResponseSchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for POST /indirect-costs/{uuid}/links')
  }
  return parsed.data
}

export async function fetchLinkedPayments(costUuid: string): Promise<IndirectCostLinksResponse> {
  const response = await apiClient.get(`/indirect-costs/${encodeURIComponent(costUuid)}/links`)
  const parsed = indirectCostLinksResponseSchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for GET /indirect-costs/{uuid}/links')
  }
  return parsed.data
}

export async function unlinkPaymentFromCost(costUuid: string, paymentUuid: string): Promise<void> {
  await apiClient.delete(
    `/indirect-costs/${encodeURIComponent(costUuid)}/links/${encodeURIComponent(paymentUuid)}`
  )
}

export default {
  createCostFromPayments,
  linkPaymentsToCost,
  fetchLinkedPayments,
  unlinkPaymentFromCost,
}
